import type { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { useAuth } from '@/context/AuthContext'
import { useCommonSignalR } from '@/context/CommonSignalRProvider'

import { useGetExpires, useRefreshSession } from './useAccount'

const refreshDebounce = [60000, 120000]

export interface LogoutTimerState {
  visible: boolean
  seconds: string
  minutes: string
  isLoggingOut: boolean
}

export const useLogoutTimer = () => {
  const { user, logout, isLoggingOut } = useAuth()
  const autoLogoffWarningTime = user?.autoLogoffWarningTime
  const { mutateAsync: refreshSession, isPending: isLoadingRefreshSession } = useRefreshSession()
  const { mutateAsync: getExpires, isPending: isLoadingGetExpires } = useGetExpires()
  const [expiresIn, setExpiresIn] = useState<Date | null>(null)

  const [state, setState] = useState<LogoutTimerState>({
    visible: false,
    seconds: '00',
    minutes: '00',
    isLoggingOut: isLoggingOut
  })

  useEffect(() => {
    refresh()
  }, [user])

  const { connection, register, unregister } = useCommonSignalR()

  useEffect(() => {
    register('LogoutRequest', () => {
      unregister('LogoutRequest')
      logout(undefined, true)
    })
  }, [connection])

  const refresh = useDebouncedCallback(
    () => {
      if (!user) return

      refreshSession()
        .then(
          ({ data }) => data.expires && setExpiresIn(new Date(data.expires)),
        )
        .catch((e: AxiosError) => {
          if (e.response?.status === 401) 
            logout()
        })
    },
    refreshDebounce[0],
    {
      maxWait: refreshDebounce[1],
      leading: true,
    },
  )

  useEffect(() => {
    const events = ['tap', 'keydown', 'scroll', 'mousemove']

    events.forEach((event) => window.addEventListener(event, refresh))

    return () => {
      events.forEach((event) => window.removeEventListener(event, refresh))
    }
  }, [])

  const tick = () => {
    if (!expiresIn) return

    if (expiresIn <= new Date()) {
      if (intervalRef.current && !isLoadingGetExpires && !isLoadingRefreshSession) {
        clearInterval(intervalRef.current!)

        getExpires()
          .then(({ data }) => {
            const expires = new Date(data.expires)
            if (expires > new Date()){
              setExpiresIn(expires)
            }
            else {
              logout()
            }
          })
          .catch(() => logout())
      }
    }

    let diff = expiresIn.getTime() - new Date().getTime()
    if (diff < 0) diff = 0

    const seconds = Math.floor(diff / 1000) % 60
    const minutes = Math.floor(diff / 60000)

    const formattedSeconds = seconds.toString().padStart(2, '0')
    const formattedMinutes = minutes.toString().padStart(2, '0')

    const visible = !!autoLogoffWarningTime && (diff < autoLogoffWarningTime)
    if ((state.visible !== visible) || (visible && (state.seconds !== formattedSeconds || state.minutes !== formattedMinutes)))
      setState({
        visible: visible,
        seconds: formattedSeconds,
        minutes: formattedMinutes,
        isLoggingOut: isLoggingOut
      })
  }

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    if (expiresIn) intervalRef.current = setInterval(tick, 200)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [expiresIn])

  return state
}
